import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@shared/ui/dialog";
import { Button } from "@shared/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/ui/card";
import { Badge } from "@shared/ui/badge";
import { ScrollArea } from "@shared/ui/scroll-area";
import { ContextualHelp } from "@shared/ui/contextual-help";
import {
  Calendar,
  Clock,
  DollarSign,
  Settings,
  Users,
  AlertCircle,
  CheckCircle,
  Info,
  Tag,
  MessageSquare,
} from "lucide-react";
import { ViewFieldProps } from "@features/cx/settings/types";
import { ServiceViewFormProps } from "@features/cx/settings/types";


// Auto-populated View Component
function ViewField({
  label,
  value,
  type = "text",
  icon,
  className = "col-span-1",
  badgeVariant = "default",
}: ViewFieldProps) {
  const renderValue = () => {
    if (value === null || value === undefined || value === "") {
      return <span className="text-muted-foreground">Not specified</span>;
    }

    switch (type) {
      case "badge":
        return <Badge variant={badgeVariant}>{value}</Badge>;

      case "boolean":
        return (
          <div className="flex items-center gap-2">
            {value ? (
              <>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <span className="text-green-700">Yes</span>
              </>
            ) : (
              <>
                <AlertCircle className="h-4 w-4 text-gray-400" />
                <span className="text-gray-600">No</span>
              </>
            )}
          </div>
        );

      case "currency":
        return (
          <div className="flex items-center gap-1">
            <DollarSign className="h-4 w-4 text-green-600" />
            <span className="font-medium">{value}</span>
          </div>
        );

      case "time":
        return (
          <div className="flex items-center gap-1">
            <Clock className="h-4 w-4 text-blue-600" />
            <span>{value} hours</span>
          </div>
        );

      case "array":
        if (Array.isArray(value)) {
          return (
            <div className="flex flex-wrap gap-1">
              {value.map((item, index) => (
                <Badge key={index} variant="outline" className="text-xs">
                  {typeof item === "object" ? item.name || item.label : item}
                </Badge>
              ))}
            </div>
          );
        }
        return <span>{value}</span>;

      default:
        return <span className="break-words">{value}</span>;
    }
  };

  return (
    <div className={`space-y-2 ${className}`}>
      <div className="flex items-center gap-2">
        {icon}
        <label className="text-sm font-medium text-gray-700">{label}</label>
      </div>
      <div className="pl-6">{renderValue()}</div>
    </div>
  );
}

// Helper function to transform service data
function getServiceData(service: any) {
  console.log(service);
  if (!service) return null;

  return {
    // Basic Information
    name: service.name,
    code: service.configurationCode,
    description: service.longDescription,
    status: service.isActive ? "Active" : "Inactive",
    dateCreated: service.createdDate,

    // Classification
    utilityServices: service.productCode,
    category: service.supportRequestTypeDisplay,
    subcategory: service.supportRequestSubtypeDisplay,

    // Configuration
    priority: service.extraData?.priorityLevelDisplay,
    serviceFee: service.extraData?.serviceCharge,
    responseTime: service.extraData?.responseTime,
    resolutionTime: service.extraData?.resolutionTime,

    // Workflow
    fieldWorkRequired: service.extraData?.fieldForceRequired,
    customerNotification: service.extraData?.consumerNotificationDisplay,
    availability: service.extraData?.availabilityDisplay,
    businessHours: service.extraData?.businessHours,
  };
}

export function ServiceViewForm({
  service,
  open,
  onClose,
  onEdit,
}: ServiceViewFormProps) {
  // Helper functions for badge variants
  const getStatusBadgeVariant = (isActive: boolean) => {
    return isActive ? "default" : "secondary";
  };

  const getPriorityBadgeVariant = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case "critical":
        return "destructive";
      case "high":
        return "default";
      case "normal":
        return "secondary";
      case "low":
        return "outline";
      default:
        return "secondary";
    }
  };

  const serviceData = getServiceData(service);

  if (!serviceData) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center">
            <div className="flex items-center gap-3">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Service Details: {serviceData.name}
              <ContextualHelp
                title="Service Information"
                content="View comprehensive details about this service configuration including classification, SLA requirements, workflow settings, and availability options."
              />
            </div>
            <Badge variant={getStatusBadgeVariant(service.isActive)}>
              {serviceData.status}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <ScrollArea className="flex-1 px-1">
          <div className="space-y-6 pr-4">
            {/* Basic Service Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Settings className="h-5 w-5" />
                  Basic Service Information
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Service Name"
                    value={serviceData.name}
                    icon={<Tag className="h-4 w-4 text-blue-600" />}
                  />
                  <ViewField
                    label="Service Code"
                    value={serviceData.code}
                    icon={<Tag className="h-4 w-4 text-purple-600" />}
                  />
                  <ViewField
                    label="Description"
                    value={serviceData.description}
                    icon={<Info className="h-4 w-4 text-gray-600" />}
                  />
                  <ViewField
                    label="Date Created"
                    value={serviceData.dateCreated || "Not available"}
                    icon={<Calendar className="h-4 w-4 text-green-600" />}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Service Classification */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Users className="h-5 w-5" />
                  Service Classification
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <ViewField
                    label="Utility Services"
                    value={serviceData.utilityServices}
                    type="array"
                    icon={<Settings className="h-4 w-4 text-blue-600" />}
                  />
                  <ViewField
                    label="Service Category"
                    value={serviceData.category}
                    icon={<Tag className="h-4 w-4 text-indigo-600" />}
                  />
                  <ViewField
                    label="Service Subcategory"
                    value={serviceData.subcategory}
                    icon={<Tag className="h-4 w-4 text-indigo-400" />}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Service Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Settings className="h-5 w-5" />
                  Service Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Priority Level"
                    value={serviceData.priority}
                    type="badge"
                    badgeVariant={getPriorityBadgeVariant(serviceData.priority)}
                    icon={<AlertCircle className="h-4 w-4 text-orange-600" />}
                  />
                  <ViewField
                    label="Service Fee"
                    value={serviceData.serviceFee}
                    icon={<DollarSign className="h-4 w-4 text-green-600" />}
                  />
                  <ViewField
                    label="Response Time"
                    value={serviceData.responseTime}
                    icon={<Clock className="h-4 w-4 text-orange-600" />}
                  />
                  <ViewField
                    label="Resolution Time"
                    value={serviceData.resolutionTime}
                    icon={<Clock className="h-4 w-4 text-orange-600" />}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Workflow Configuration */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Settings className="h-5 w-5" />
                  Workflow Configuration
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ViewField
                    label="Field Work Required"
                    value={serviceData.fieldWorkRequired}
                    type="boolean"
                  />
                  <ViewField
                    label="Customer Notification"
                    value={serviceData.customerNotification}
                    icon={<Users className="h-4 w-4 text-green-600" />}
                  />
                  <ViewField
                    label="Availability Settings"
                    value={serviceData.availability}
                    icon={<Clock className="h-4 w-4 text-blue-600" />}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </ScrollArea>

        <DialogFooter className="flex-shrink-0 mt-4">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {onEdit && <Button onClick={onEdit}>Edit Service</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}